import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CalendarDays, ChevronRight, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

/**
 * Parse a show's "time" string ("12-5AM", "10-12AM", "11-12PM", "7-10PM",
 * "10-1AM", etc.) into [startHour24, endHour24] in station local time.
 *
 * The AM/PM suffix at the end of the string applies to the END time.
 * We infer the START's AM/PM by checking: if the start interpreted with
 * the SAME suffix ends up >= the end, the show wraps around the noon/
 * midnight boundary, so the start belongs to the opposite half of the day.
 * If even that flip can't make start < end, the show wraps past midnight
 * and we add 24 to the end so the range is monotonic.
 * Returns null if the string can't be parsed.
 */
function parseHourRange(timeStr) {
  if (!timeStr) return null;
  // Supports "12-5AM", "11-12PM", "7-10PM", "10-1AM", "6-7:30AM", "9-11:45PM".
  // The AM/PM suffix at the end applies to the END time; each side may carry
  // optional :MM minutes.
  const m = String(timeStr)
    .toUpperCase()
    .replace(/\s+/g, "")
    .match(/^(\d{1,2})(?::(\d{2}))?-(\d{1,2})(?::(\d{2}))?(AM|PM)$/);
  if (!m) return null;
  const startNum = parseInt(m[1], 10);
  const startMin = m[2] ? parseInt(m[2], 10) / 60 : 0;
  const endNum = parseInt(m[3], 10);
  const endMin = m[4] ? parseInt(m[4], 10) / 60 : 0;
  const suffix = m[5];

  const to24 = (n, suf) => {
    if (suf === "AM") return n === 12 ? 0 : n;
    return n === 12 ? 12 : n + 12;
  };

  let endH = to24(endNum, suffix) + endMin;
  let startH = to24(startNum, suffix) + startMin;
  if (startH >= endH) {
    startH = to24(startNum, suffix === "AM" ? "PM" : "AM") + startMin;
    if (startH >= endH) {
      endH += 24; // wraps past midnight
    }
  }
  return [startH, endH];
}

function parseStartHour(timeStr) {
  const r = parseHourRange(timeStr);
  return r ? r[0] : null;
}

/**
 * Slim home-page teaser that replaces the full 7-day Schedule grid.
 * Shows just the show that's airing right now (live=true) plus a CTA to
 * the dedicated /schedule page. Loads ONE API call and renders very fast.
 */
export default function NowLiveTeaser() {
  const [current, setCurrent] = useState(null); // {name, time, dj_name, dj_slug, live}
  const [next, setNext] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/schedule`);
        const schedule = res.data?.schedule || {};
        const now = new Date();
        const todayKey = DAY_KEYS[now.getDay()];
        const yesterdayKey = DAY_KEYS[(now.getDay() + 6) % 7];
        const tomorrowKey = DAY_KEYS[(now.getDay() + 1) % 7];
        const nowHour = now.getHours() + now.getMinutes() / 60;
        const todayShows = schedule[todayKey] || [];
        const yShows = schedule[yesterdayKey] || [];
        const tShows = schedule[tomorrowKey] || [];
        if (cancelled) return;

        // Find the show currently airing by time slot.
        // 1. Check today: startH <= now < endH
        // 2. If nothing matches and we're in early-morning hours, check if
        //    yesterday's last show wraps past midnight into today.
        let currentShow = null;
        let currentIdx = -1;
        for (let i = 0; i < todayShows.length; i++) {
          const r = parseHourRange(todayShows[i].time);
          if (!r) continue;
          const [s, e] = r;
          if (nowHour >= s && nowHour < e) {
            currentShow = todayShows[i];
            currentIdx = i;
            break;
          }
        }
        if (!currentShow && yShows.length) {
          // Check if yesterday's last show wraps into today.
          const lastY = yShows[yShows.length - 1];
          const r = parseHourRange(lastY.time);
          if (r && r[1] > 24 && nowHour < r[1] - 24) {
            currentShow = lastY;
          }
        }

        // Up Next = first show AFTER the current one's slot.
        let upNext = null;
        if (currentIdx >= 0 && currentIdx + 1 < todayShows.length) {
          upNext = todayShows[currentIdx + 1];
        }
        if (!upNext) {
          upNext =
            todayShows.find((s) => {
              const sh = parseStartHour(s.time);
              return sh != null && sh > nowHour;
            }) || null;
        }
        if (!upNext) {
          upNext = tShows[0] || null;
        }

        setCurrent(currentShow);
        setNext(upNext);
      } catch (e) {
        /* silent */
      }
    };
    load();
    // Refresh every 5 minutes so the LIVE badge stays in sync without
    // bloating the page like the full schedule did.
    const t = setInterval(load, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  return (
    <section
      id="now-live"
      className="relative border-t border-zinc-800 bg-zinc-950"
      data-testid="now-live-teaser"
    >
      <div className="px-4 md:px-10 py-10 md:py-14">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-3">
              //_ON_AIR_NOW
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-none text-zinc-50 glitch">
              ON THE MIC<span className="text-red-600">.</span>
            </h2>
          </div>
          <Link
            to="/schedule"
            className="inline-flex items-center gap-2 border border-zinc-700 hover:border-red-600 hover:bg-red-600 hover:text-black text-zinc-100 font-display tracking-[0.25em] uppercase text-xs px-4 py-3 transition-colors"
            data-testid="now-live-view-full-schedule"
          >
            <CalendarDays className="w-4 h-4" />
            Full Weekly Schedule
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 border border-zinc-800">
          {/* CURRENTLY AIRING card */}
          <div
            className="relative p-6 md:p-8 border-b md:border-b-0 md:border-r border-zinc-800 bg-zinc-950 min-h-[180px] flex flex-col justify-between"
            data-testid="now-live-current-card"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300">
                //_ON_AIR_NOW
              </div>
              {current && current.live && (
                <span
                  className="text-[10px] tracking-widest uppercase bg-red-600 text-black px-2 py-0.5 font-display whitespace-nowrap"
                  data-testid="now-live-badge"
                >
                  ● LIVE
                </span>
              )}
              {current && !current.live && (
                <span
                  className="text-[10px] tracking-widest uppercase bg-zinc-100 text-black px-2 py-0.5 font-display whitespace-nowrap"
                  data-testid="now-prerecorded-badge"
                >
                  PRE-RECORDED
                </span>
              )}
            </div>

            {current ? (
              <div className="mt-4">
                <div className="font-display text-2xl md:text-3xl leading-tight tracking-tight text-zinc-50 mb-2">
                  {current.name}
                </div>
                <div className="text-xs tracking-widest uppercase text-zinc-300 mb-3">
                  {current.time}
                </div>
                {current.dj_name && (
                  <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300">
                    //_host:{" "}
                    {current.dj_slug ? (
                      <Link
                        to={`/djs/${current.dj_slug}`}
                        className="text-red-500 hover:text-red-400"
                        data-testid={`now-live-host-${current.dj_slug}`}
                      >
                        {current.dj_name}
                      </Link>
                    ) : (
                      <span className="text-zinc-100">{current.dj_name}</span>
                    )}
                  </div>
                )}
                {current.dj_slug && (
                  <Link
                    to={`/djs/${current.dj_slug}`}
                    className="mt-4 inline-flex items-center gap-1 text-[10px] tracking-widest uppercase border border-zinc-700 px-2 py-1 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
                    data-testid={`now-live-meet-dj-${current.dj_slug}`}
                  >
                    <User className="w-3 h-3" /> Meet DJ
                  </Link>
                )}
              </div>
            ) : (
              <div className="mt-4">
                <div className="font-display text-2xl md:text-3xl leading-tight tracking-tight text-zinc-50 mb-2">
                  AUTO-PILOT MIX
                </div>
                <div className="text-xs text-zinc-300 leading-relaxed">
                  No show scheduled in this slot — pure rock & metal rotation
                  running 24/7. Check the full schedule to see who's up next.
                </div>
              </div>
            )}
          </div>

          {/* UP NEXT card */}
          <div
            className="relative p-6 md:p-8 bg-zinc-950 min-h-[180px] flex flex-col justify-between"
            data-testid="now-live-next-card"
          >
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300">
              //_UP_NEXT_TODAY
            </div>

            {next ? (
              <div className="mt-4">
                <div className="font-display text-2xl md:text-3xl leading-tight tracking-tight text-zinc-50 mb-2">
                  {next.name}
                </div>
                <div className="text-xs tracking-widest uppercase text-zinc-300 mb-3">
                  {next.time}
                </div>
                {next.dj_name && (
                  <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300">
                    //_host:{" "}
                    {next.dj_slug ? (
                      <Link
                        to={`/djs/${next.dj_slug}`}
                        className="text-red-500 hover:text-red-400"
                        data-testid={`now-live-next-host-${next.dj_slug}`}
                      >
                        {next.dj_name}
                      </Link>
                    ) : (
                      <span className="text-zinc-100">{next.dj_name}</span>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-4">
                <div className="font-display text-xl md:text-2xl leading-tight tracking-tight text-zinc-100 mb-2">
                  No more shows scheduled today.
                </div>
                <Link
                  to="/schedule"
                  className="text-xs tracking-widest uppercase text-red-500 hover:text-red-400"
                  data-testid="now-live-see-tomorrow"
                >
                  ▸ See tomorrow's lineup
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 text-xs text-zinc-600 tracking-widest uppercase">
          //_station_local_time · auto_updates_every_5_min
        </div>
      </div>
    </section>
  );
}
